import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { buildApiUrl } from '../utils/api';

// Detectar dispositivo
const detectarDispositivo = () => {
  const userAgent = navigator.userAgent;
  if (/Mobile|Android|iPhone/i.test(userAgent)) {
    return 'mobile';
  } else if (/iPad|Tablet/i.test(userAgent)) {
    return 'tablet';
  }
  return 'desktop';
};

// Detectar navegador
const detectarNavegador = () => {
  const userAgent = navigator.userAgent;
  // Verificar Edge PRIMEIRO (Edge contém "Chrome" no user agent)
  if (/Edg/i.test(userAgent)) return 'Edge';
  if (/Chrome/i.test(userAgent)) return 'Chrome';
  if (/Firefox/i.test(userAgent)) return 'Firefox';
  if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) return 'Safari';
  if (/MSIE|Trident/i.test(userAgent)) return 'Internet Explorer';
  return 'Outro';
};

// Enviar evento para o backend
const enviarEvento = async (tipo, pagina, detalhes = null, duracao = null) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return Promise.resolve();
    }

    const payload = {
      tipo,
      pagina,
      dispositivo: detectarDispositivo(),
      navegador: detectarNavegador(),
      detalhes
    };

    // Adicionar duração se fornecida
    if (duracao !== null && duracao > 0) {
      payload.duracao = duracao;
    }

    const response = await fetch(buildApiUrl('/api/acessos'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('[TRACKING] Erro ao enviar evento:', errorData);
    }
    
    return Promise.resolve();
  } catch (error) {
    console.error('[TRACKING] Exceção ao enviar evento:', error);
    return Promise.resolve();
  }
};

// Enviar evento via sendBeacon (para beforeunload)
const enviarEventoBeacon = (tipo, pagina, duracao) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const payload = {
      tipo,
      pagina,
      dispositivo: detectarDispositivo(),
      navegador: detectarNavegador(),
      duracao
    };

    // Usar fetch com keepalive em vez de sendBeacon para suportar headers
    fetch(buildApiUrl('/api/acessos'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {
      // Silenciosamente ignorar erros em beforeunload
    });
  } catch (error) {
    console.debug('Erro ao enviar beacon:', error);
  }
};

// Hook principal de tracking
export const useTracking = () => {
  const location = useLocation();
  const entradaTimestampRef = useRef(null);
  const paginaAtualRef = useRef(null);
  const ultimoEnvioRef = useRef({ eventoId: null, timestamp: 0 });
  
  useEffect(() => {
    // Ignorar páginas de autenticação
    const paginasIgnoradas = ['/login', '/microsoft-login', '/microsoft-login-success', '/microsoft-login-error'];
    if (paginasIgnoradas.includes(location.pathname)) {
      return;
    }

    const paginaAtual = location.pathname;
    
    // IGNORAR se a página não mudou de verdade (React Strict Mode)
    if (paginaAtualRef.current === paginaAtual) {
      return;
    }
    
    // Se mudou de página, enviar duração da página ANTERIOR
    if (paginaAtualRef.current && entradaTimestampRef.current) {
      const duracaoSegundos = Math.floor((Date.now() - entradaTimestampRef.current) / 1000);
      
      // Criar identificador único para evitar duplicatas
      const eventoId = `${paginaAtualRef.current}-${entradaTimestampRef.current}`;
      const agora = Date.now();
      
      // Verificar se:
      // 1. Não é o mesmo evento OU
      // 2. Se for o mesmo evento, passou pelo menos 500ms desde o último envio
      const isDiferente = ultimoEnvioRef.current.eventoId !== eventoId;
      const passouTempo = agora - ultimoEnvioRef.current.timestamp > 500;
      
      if (isDiferente || passouTempo) {
        // Marcar IMEDIATAMENTE antes de enviar (proteção contra race condition)
        ultimoEnvioRef.current = {
          eventoId: eventoId,
          timestamp: agora
        };
        
        // SEMPRE enviar, mesmo se < 1 segundo (registrar todas as visualizações)
        enviarEvento('pageview', paginaAtualRef.current, null, duracaoSegundos);
      }
    }
    
    // Atualizar para nova página
    paginaAtualRef.current = paginaAtual;
    entradaTimestampRef.current = Date.now();

  }, [location.pathname]);

  // Registrar duração ao sair/recarregar página
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (paginaAtualRef.current && entradaTimestampRef.current) {
        const duracaoSegundos = Math.floor((Date.now() - entradaTimestampRef.current) / 1000);
        enviarEventoBeacon('pageview', paginaAtualRef.current, duracaoSegundos);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Função para trackear eventos customizados
  const trackEvent = async (tipo, detalhes = null) => {
    await enviarEvento(tipo, location.pathname, detalhes);
  };

  // Função para trackear login
  const trackLogin = async () => {
    await enviarEvento('login', '/login', 'Usuário autenticado com sucesso');
  };

  // Função para trackear logout
  const trackLogout = async () => {
    await enviarEvento('logout', location.pathname, 'Usuário saiu do sistema');
  };

  return { trackEvent, trackLogin, trackLogout };
};

export default useTracking;


